import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Tooltip,
  Divider,
  useTheme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  WaterDrop as WaterDropIcon,
} from '@mui/icons-material';
import { useAppTheme } from '../../contexts/ThemeContext';

const Sidebar = ({ open, toggleDrawer, handleLogout, onToggle }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const muiTheme  = useTheme();
  const isMobile  = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { palette } = useAppTheme();
  const sb = palette.sidebar;

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  const toggleCollapsed = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    if (onToggle) onToggle(next);
  };

  const drawerWidth = isCollapsed ? 80 : 240;

  const menuItems = [
    { icon: <DashboardIcon />, label: 'Dashboard',   path: '/dashboard' },
    { icon: <HomeIcon />,      label: 'Properties',  path: '/properties' },
    { icon: <PeopleIcon />,    label: 'Tenants',     path: '/tenants' },
    { icon: <WaterDropIcon />, label: 'Water Bills', path: '/water-bills' },
    { icon: <ReceiptIcon />,   label: 'Payments',    path: '/payments' },
    { icon: <AssessmentIcon />,label: 'Reports',     path: '/reports' },
    { icon: <SettingsIcon />,  label: 'Settings',    path: '/settings' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) toggleDrawer();
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={toggleDrawer}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: muiTheme.transitions.create('width', {
          easing: muiTheme.transitions.easing.sharp,
          duration: muiTheme.transitions.duration.enteringScreen,
        }),
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          transition: muiTheme.transitions.create('width', {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          backgroundColor: sb.background,
          borderRight: `1px solid ${palette.border}`,
        },
      }}
    >
      {/* ── Logo / brand ───────────────────────────────── */}
      <Box
        sx={{
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 1.5 : 2,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0, right: 0, bottom: 0,
            height: '1px',
            backgroundColor: palette.border,
          },
        }}
      >
        <Box
          component="img"
          src="/logo.png"
          alt="Teknant logo"
          sx={{
            width: isCollapsed ? 44 : 48,
            height: isCollapsed ? 44 : 48,
            objectFit: 'contain',
            borderRadius: '10px',
            flexShrink: 0,
            transition: 'width 0.3s ease, height 0.3s ease',
          }}
        />

        {!isCollapsed && (
          <Box sx={{ ml: 1.5, display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <Typography
              sx={{
                fontWeight: 800,
                color: sb.activeText,
                fontSize: '1.25rem',
                letterSpacing: '0.06em',
                lineHeight: 1.1,
                fontFamily: '"Segoe UI", system-ui, sans-serif',
              }}
            >
              TEKNANT
            </Typography>
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: palette.text.secondary,
                letterSpacing: '0.04em',
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              Property Management
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Menu content ───────────────────────────────── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 80px)', overflow: 'hidden' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List sx={{ py: 0 }}>
            {menuItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <Box key={item.label}>
                  <Tooltip title={isCollapsed ? item.label : ''} placement="right">
                    <ListItem
                      button
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        minHeight: 65,
                        px: 2.5,
                        py: 2,
                        position: 'relative',
                        backgroundColor: isActive ? sb.activeBackground : 'transparent',
                        '&:hover': { backgroundColor: sb.hoverBackground },
                        ...(isActive && {
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0, top: 0, bottom: 0,
                            width: '3px',
                            backgroundColor: sb.activeBorder,
                          },
                        }),
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isCollapsed ? 0 : 2,
                          justifyContent: 'center',
                          color: isActive ? sb.activeIcon : sb.icon,
                          '& .MuiSvgIcon-root': {
                            fontSize: isCollapsed ? 28 : 22,
                            transition: 'font-size 0.3s ease',
                          },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!isCollapsed && (
                        <ListItemText
                          primary={item.label}
                          sx={{
                            '& .MuiTypography-root': {
                              color: isActive ? sb.activeText : sb.text,
                              fontSize: '0.95rem',
                              fontWeight: isActive ? 650 : 500,
                            },
                          }}
                        />
                      )}
                    </ListItem>
                  </Tooltip>
                  {index < menuItems.length - 1 && (
                    <Divider sx={{ borderColor: palette.border, opacity: 0.6 }} />
                  )}
                </Box>
              );
            })}
          </List>

          {/* Logout */}
          <Divider sx={{ borderColor: palette.border, opacity: 0.6, my: 1 }} />
          <List>
            <Tooltip title={isCollapsed ? 'Logout' : ''} placement="right">
              <ListItem
                button
                onClick={handleLogout}
                sx={{
                  minHeight: 65,
                  px: 2.5,
                  py: 2,
                  '&:hover': { backgroundColor: 'rgba(239,68,68,0.08)' },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isCollapsed ? 0 : 2,
                    justifyContent: 'center',
                    color: sb.icon,
                    '& .MuiSvgIcon-root': {
                      fontSize: isCollapsed ? 28 : 22,
                      transition: 'font-size 0.3s ease',
                    },
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary="Logout"
                    sx={{
                      '& .MuiTypography-root': {
                        color: sb.text,
                        fontSize: '0.95rem',
                        fontWeight: 500,
                      },
                    }}
                  />
                )}
              </ListItem>
            </Tooltip>
          </List>
        </Box>

        {/* Collapse / expand toggle */}
        {!isMobile && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
              <IconButton
                onClick={toggleCollapsed}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: sb.background,
                  border: `1px solid ${palette.border}`,
                  boxShadow: 1,
                  color: sb.activeIcon,
                  '&:hover': {
                    backgroundColor: sb.hoverBackground,
                    boxShadow: 2,
                  },
                }}
              >
                {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Sidebar;